import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Event, NavigationEnd, Router, RouterEvent } from '@angular/router';
import {
  NON_PUBLIC_PAGES,
  NOT_TRANSLATED_SUB_PAGES,
  PAGES_WITHOUT_TRANSLATION,
  ROUTES_WITH_DISABLED_LANG_SWITCHER,
} from '@core/api/constants/not-translated-pages.constants';
import { TRANSLATED_URLS, TTranslatedUrl } from '@core/api/constants/translated-urls.constants';
import { environment } from '@env/environment';
import { ISimple } from '@noda-lib/utils/interfaces';
import { NODA_WEB_LINK } from '@shared/constants/noda-links';
import { AVAILABLE_LANGS, DEFAULT_LANG } from '@translate/constants/available-langs';
import { LanguageService } from '@translate/services/language.service';
import { parsePath } from '@translate/utils/parse-lang-path';
import { from } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { IDuplicateArticleData } from '../../../../config/sitemap';

@Injectable({
  providedIn: 'root',
})
export class CanonicalService {
  private router: Router = inject(Router);
  private languageService: LanguageService = inject(LanguageService);
  protected document: Document = inject(DOCUMENT);

  public setCanonicalURL(): void {
    this.router.events
      .pipe(
        filter(
          (event: Event | RouterEvent): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        switchMap(() => {
          const url: URL = new URL(this.router.url, NODA_WEB_LINK);
          const { cleanPath } = parsePath(url.pathname);
          const normalizedPath: string = cleanPath.replace(/^\//, '');

          this.addCanonicalTag(`${url.origin}${url.pathname}`);

          return from(this.addAlternateLinks(normalizedPath));
        }),
      )
      .subscribe();
  }

  private addCanonicalTag(href: string): void {
    let link: HTMLLinkElement = this.document.querySelector("link[rel='canonical']");
    if (link) {
      link.href = href;
    } else {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = href;
      this.document.head.appendChild(link);
    }
  }

  private async addAlternateLinks(cleanPath: string): Promise<void> {
    this.removeExistingAlternateLinks();
    await this.setupAlternateLinks(cleanPath);
  }

  private removeExistingAlternateLinks(): void {
    const links: NodeListOf<Element> = this.document.querySelectorAll("link[rel='alternate']");
    links.forEach(link => link.parentNode?.removeChild(link));
  }

  private async setupAlternateLinks(cleanPath: string): Promise<void> {
    if (cleanPath.startsWith('articles/')) {
      await this.setupArticleAlternateLinks(cleanPath);

      return;
    }

    const translatedPathLanguageMap: ISimple<string> = this.getPathLanguageMap(TRANSLATED_URLS);

    if (NON_PUBLIC_PAGES.some(page => cleanPath.includes(page))) return;

    if (ROUTES_WITH_DISABLED_LANG_SWITCHER.some(page => cleanPath.includes(page))) {
      const { activeLang } = this.languageService;
      this.appendAlternateLink(activeLang, this.getLangHref(activeLang, cleanPath));

      return;
    }

    if (PAGES_WITHOUT_TRANSLATION.some(page => cleanPath.includes(page))) {
      this.addLinksForUntranslatedPage(cleanPath);

      return;
    }

    if (translatedPathLanguageMap[cleanPath]) {
      this.addLinksForPageWithTranslatedUrl(translatedPathLanguageMap[cleanPath]);

      return;
    }

    if (TRANSLATED_URLS[cleanPath]) {
      this.addLinksForPageWithTranslatedUrl(cleanPath);

      return;
    }

    if (NOT_TRANSLATED_SUB_PAGES.some(page => cleanPath.includes(page))) {
      this.addLinksForPartiallyTranslatedPage(cleanPath);

      return;
    }

    this.addLinksForFullyTranslatedPage(cleanPath);
  }

  private async setupArticleAlternateLinks(cleanPath: string): Promise<void> {
    const currentFullPath: string = this.router.url.replace(/^\/+/, '').replace(/\/+$/, '');
    const duplicates: IDuplicateArticleData[] = await this.getArticleDuplicateURLs();

    const match: IDuplicateArticleData | undefined = duplicates.find(
      (article: IDuplicateArticleData) => {
        const urls: string[] = [
          article.mainUrl,
          ...article.urls.map((u: any) => u.dublicates),
        ];

        return urls.some((url: string) => {
          const relative: string = url.replace(NODA_WEB_LINK + '/', '').replace(/^\/|\/$/g, '');

          return relative === currentFullPath;
        });
      },
    );

    if (!match) {
      const lang: string = this.languageService.activeLang;
      const href: string = this.getLangHref(lang, cleanPath);
      this.appendAlternateLink(lang, href);

      if (lang === DEFAULT_LANG) {
        this.appendAlternateLink('x-default', href);
      }

      return;
    }

    const mainUrlLang: string = AVAILABLE_LANGS.some(
      lang => match.mainUrl.split('/').filter(Boolean)[2] === lang,
    )
      ? match.mainUrl.split('/').filter(Boolean)[2]
      : 'en';

    const allVersions: { lang: string; href: string }[] = [
      { lang: mainUrlLang, href: match.mainUrl },
      ...match.urls.map(u => ({
        lang: u.language.value,
        href: u.dublicates,
      })),
    ];

    const langs: string[] = allVersions.map(v => v.lang);

    if (langs.length === 1 && langs[0] === 'en') {
      this.appendAlternateLink('en', match.mainUrl);
      this.appendAlternateLink('x-default', match.mainUrl);

      return;
    }

    if (langs.includes('en')) {
      allVersions.forEach(({ lang, href }) => {
        this.appendAlternateLink(lang, href);
      });

      const xDefaultUrl: string =
        allVersions.find(v => v.lang === DEFAULT_LANG)?.href || match.mainUrl;

      this.appendAlternateLink('x-default', xDefaultUrl);

      return;
    }

    allVersions.forEach(({ lang, href }) => {
      this.appendAlternateLink(lang, href);
    });
  }

  private appendAlternateLink(hreflang: string, href: string): void {
    const link: HTMLLinkElement = this.document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    this.document.head.appendChild(link);
  }

  private addLinksForUntranslatedPage(cleanPath: string): void {
    const defaultHref: string = this.getLangHref(DEFAULT_LANG, cleanPath);
    this.appendAlternateLink(DEFAULT_LANG, defaultHref);
    this.appendAlternateLink('x-default', defaultHref);
  }

  private addLinksForPartiallyTranslatedPage(cleanPath: string): void {
    const { activeLang } = this.languageService;
    this.appendAlternateLink(activeLang, this.getLangHref(activeLang, cleanPath));
    this.appendAlternateLink('x-default', this.getLangHref(DEFAULT_LANG, cleanPath));
  }

  private addLinksForFullyTranslatedPage(cleanPath: string): void {
    this.appendAlternateLink('x-default', this.getLangHref(DEFAULT_LANG, cleanPath));
    AVAILABLE_LANGS.forEach(lang => {
      this.appendAlternateLink(lang, this.getLangHref(lang, cleanPath));
    });
  }

  private addLinksForPageWithTranslatedUrl(cleanPath: string): void {
    this.appendAlternateLink('x-default', this.getLangHref(DEFAULT_LANG, cleanPath));
    const translations: ISimple<string> = TRANSLATED_URLS[cleanPath];

    if (translations) {
      this.appendAlternateLink(DEFAULT_LANG, this.getLangHref(DEFAULT_LANG, cleanPath));

      Object.entries(translations).forEach(([lang, translatedPath]) => {
        this.appendAlternateLink(lang, this.getLangHref(lang, translatedPath));
      });

      AVAILABLE_LANGS.forEach(lang => {
        if (lang !== DEFAULT_LANG && !translations[lang]) {
          this.appendAlternateLink(lang, this.getLangHref(lang, cleanPath));
        }
      });
    } else {
      AVAILABLE_LANGS.forEach(lang => {
        this.appendAlternateLink(lang, this.getLangHref(lang, cleanPath));
      });
    }
  }

  private getLangHref(lang: string, cleanPath: string): string {
    return lang === DEFAULT_LANG
      ? `${NODA_WEB_LINK}/${cleanPath}`
      : `${NODA_WEB_LINK}/${lang}/${cleanPath}`.replace(/\/$/, '');
  }

  private getPathLanguageMap(translatedUrls: TTranslatedUrl): ISimple<string> {
    const result: ISimple<string> = {};

    for (const [parentKey, translatedUrl] of Object.entries(translatedUrls)) {
      for (const translatedPath of Object.values(translatedUrl)) {
        result[translatedPath] = parentKey;
      }
    }

    return result;
  }

  private async getArticleDuplicateURLs(): Promise<IDuplicateArticleData[]> {
    try {
      const url: URL = new URL(`${environment.APP_STRAPI_HOST}/article-url-dublicates`);
      url.search = new URLSearchParams({
        'populate[urls][populate][language][populate]': '*',
      }).toString();

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Authorization: `Bearer ${environment.APP_STRAPI_TOKEN}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return [];

      const result = await response.json();

      return result?.data || [];
    } catch (error) {
      console.error('Failed to fetch article duplicates:', error);

      return [];
    }
  }
}
